import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import "./Home.css";
import useSEO from "../../hooks/useSEO";

/* ─────────────────────────────────────────
   ASSETS
───────────────────────────────────────── */
const imgExcel   = require("../../assets/images/TutorialsCard/basic_Excel.WebP");
const imgSQL     = require("../../assets/images/TutorialsCard/basic_SQL.WebP");
const imgPython  = require("../../assets/images/TutorialsCard/basic_Python.WebP");

//const heroLightImage = require("../../assets/images/home_rightside_backgound_light_mode.WebP");
//const heroDarkImage  = require("../../assets/images/home_rightside_backgound_dark_mode.WebP");

const iconExcel       = require("../../assets/icons/excel.WebP");
const iconSQL         = require("../../assets/icons/sql.WebP");
const iconPython      = require("../../assets/icons/python.WebP");
const iconPowerBI     = require("../../assets/icons/powerbi.WebP");
const iconPandas      = require("../../assets/icons/Pandas_icon.WebP");
const iconDataClean   = require("../../assets/icons/Data_cleaning_icon.WebP");
const iconAutomation  = require("../../assets/icons/automation_icon.WebP");

/* ─────────────────────────────────────────
   CONSTANTS
───────────────────────────────────────── */
const SKILLS = ["Excel", "SQL", "Python", "Power BI", "Dashboards", "Analytics"];

const COURSES = [
  {
    id: 1,
    title: "Excel Fundamentals",
    level: "Beginner",
    image: imgExcel,
    route: "/tutorials/basic-excel",
    tag: "Most Popular",
    tagColor: "#6153e0",
    desc: "Build spreadsheets, clean data, create reports, and learn Excel workflows used in real work.",
    topics: ["Formulas", "Pivot Tables", "Charts", "Data Cleaning"],
    icon: "📊",
  },
  {
    id: 2,
    title: "SQL Essentials",
    level: "Beginner",
    image: imgSQL,
    route: "/tutorials/basic-sql",
    tag: "Career Skill",
    tagColor: "#0891d8",
    desc: "Learn how to query databases, filter information, join tables, and answer business questions.",
    topics: ["SELECT", "WHERE", "JOIN", "GROUP BY"],
    icon: "🗄️",
  },
  {
    id: 3,
    title: "Python Basics",
    level: "Beginner",
    image: imgPython,
    route: "/tutorials/python-basics",
    tag: "High Demand",
    tagColor: "#059669",
    desc: "Start Python with logic, loops, functions, automation basics, and beginner data workflows.",
    topics: ["Variables", "Functions", "Loops", "Pandas Intro"],
    icon: "🐍",
  },
];

const STATS = [
  { value: "3",    suffix: "+", label: "Core Tracks",    icon: "👥" },
  { value: "100",  suffix: "%", label: "Free to Start",  icon: "🎁" },
  { value: "Real", suffix: "",  label: "Career Skills",  icon: "⭐" },
];

const FEATURES = [
  {
    icon: "📊",
    title: "Learn by Doing",
    desc: "Every lesson is built around practical tasks, real files, and job-style workflows instead of passive theory.",
    accent: "#6153e0",
  },
  {
    icon: "⚡",
    title: "Structured Growth",
    desc: "Follow a guided path that takes you from complete beginner to confident and consistent data worker.",
    accent: "#0891d8",
  },
  {
    icon: "🎯",
    title: "Built for Real Careers",
    desc: "Focus on Excel, SQL, Python, and Power BI — the skills companies actually use in real data roles.",
    accent: "#059669",
  },
];

const OUTCOMES = [
  {
    icon: "📈",
    title: "Data Analyst Foundations",
    desc: "Use Excel, SQL, and dashboards to build the skills that open doors to analyst roles.",
    tag: "Analyst Path",
    accent: "#6153e0",
  },
  {
    icon: "🤖",
    title: "Automation Skill Builder",
    desc: "Use Python and practical workflows to save time, automate tasks, and work smarter.",
    tag: "Python Path",
    accent: "#0891d8",
  },
  {
    icon: "💼",
    title: "Freelance-Ready Skills",
    desc: "Develop practical project skills you can use for internships, freelance work, and portfolio growth.",
    tag: "Career Path",
    accent: "#059669",
  },
];

const PATH = [
  {
    no: "01",
    title: "Start With Strong Basics",
    desc: "Begin with Excel, SQL, Python, and clear data thinking without feeling overwhelmed.",
  },
  {
    no: "02",
    title: "Practice Real Workflows",
    desc: "Apply what you learn through examples, tasks, and problem-solving that feel practical.",
  },
  {
    no: "03",
    title: "Grow Into Job-Ready Skills",
    desc: "Move into dashboards, reporting, automation, and projects that build real confidence.",
  },
];

const TRUST_ITEMS = [
  { label: "Excel",        icon: iconExcel },
  { label: "SQL",          icon: iconSQL },
  { label: "Python",       icon: iconPython },
  { label: "Power BI",     icon: iconPowerBI },
  { label: "Pandas",       icon: iconPandas },
  { label: "Dashboards",   icon: iconPowerBI },
  { label: "Analytics",    icon: iconExcel },
  { label: "Automation",   icon: iconAutomation },
  { label: "Reporting",    icon: iconPowerBI },
  { label: "Data Cleaning",icon: iconDataClean },
  // duplicate set for seamless loop
  { label: "Excel",        icon: iconExcel },
  { label: "SQL",          icon: iconSQL },
  { label: "Python",       icon: iconPython },
  { label: "Power BI",     icon: iconPowerBI },
  { label: "Pandas",       icon: iconPandas },
  { label: "Dashboards",   icon: iconPowerBI },
  { label: "Analytics",    icon: iconExcel },
  { label: "Automation",   icon: iconAutomation },
  { label: "Reporting",    icon: iconPowerBI },
  { label: "Data Cleaning",icon: iconDataClean },
];

/* ─────────────────────────────────────────
   FRAMER-MOTION VARIANTS
───────────────────────────────────────── */
const heroContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.11, delayChildren: 0.06 },
  },
};

const heroItem = {
  hidden: { opacity: 0, y: 24, filter: "blur(10px)" },
  show: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.72, ease: [0.16, 1, 0.3, 1] },
  },
};

const revealCard = {
  hidden: { opacity: 0, y: 32, scale: 0.97 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { delay: i * 0.09, duration: 0.62, ease: [0.16, 1, 0.3, 1] },
  }),
};

const viewport = { once: true, amount: 0.18 };

/* ─────────────────────────────────────────
   SMALL COMPONENTS
───────────────────────────────────────── */
function ArrowIcon({ size = 15 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
  );
}

function SectionLabel({ children }) {
  return (
    <div className="section-label-wrap">
      <span className="section-label">{children}</span>
    </div>
  );
}

/* ─────────────────────────────────────────
   HERO VISUAL — Interactive animated panel
───────────────────────────────────────── */
const SKILL_BARS = [
  { label: "Excel",    icon: "📊", pct: 85, color: "#22c55e" },
  { label: "SQL",      icon: "🗄️", pct: 68, color: "#3b82f6" },
  { label: "Python",   icon: "🐍", pct: 74, color: "#f59e0b" },
  { label: "Power BI", icon: "📈", pct: 62, color: "#a855f7" },
];

const CODE_LINES = [
  { txt: "import pandas as pd",          color: "#a78bfa" },
  { txt: 'df = pd.read_csv("data.csv")', color: "#f0f4fc" },
  { txt: "df.head()",                    color: "#34d399" },
  { txt: "df.describe()",                color: "#34d399" },
  { txt: 'df["salary"].mean()',          color: "#60a5fa" },
];

function AnimatedBar({ pct, color, delay }) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setWidth(pct), 400 + delay);
    return () => clearTimeout(t);
  }, [pct, delay]);
  return (
    <div className="hv-bar-track">
      <div
        className="hv-bar-fill"
        style={{ width: `${width}%`, background: color, transitionDelay: `${delay}ms` }}
      />
    </div>
  );
}

function TypingCode() {
  const [visibleLines, setVisibleLines] = useState(0);
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    // When all lines are typed, pause then restart from the top
    if (visibleLines >= CODE_LINES.length) {
      const t = setTimeout(() => {
        setVisibleLines(0);
        setCharCount(0);
      }, 1800);
      return () => clearTimeout(t);
    }
    const line = CODE_LINES[visibleLines].txt;
    if (charCount < line.length) {
      const t = setTimeout(() => setCharCount(c => c + 1), 38);
      return () => clearTimeout(t);
    } else {
      const t = setTimeout(() => {
        setVisibleLines(v => v + 1);
        setCharCount(0);
      }, 320);
      return () => clearTimeout(t);
    }
  }, [visibleLines, charCount]);

  return (
    <div className="hv-code-block">
      <div className="hv-code-dots">
        <span style={{ background: "#ff5f57" }} />
        <span style={{ background: "#febc2e" }} />
        <span style={{ background: "#28c840" }} />
      </div>
      <div className="hv-code-lines">
        {CODE_LINES.map((line, i) => (
          <div key={i} className="hv-code-line">
            <span className="hv-line-num">{i + 1}</span>
            <span style={{ color: line.color }}>
              {i < visibleLines
                ? line.txt
                : i === visibleLines
                ? line.txt.slice(0, charCount)
                : ""}
              {i === visibleLines && <span className="hv-cursor">|</span>}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function HeroVisual() {
  const [progress, setProgress] = useState(0);
  const [streak, setStreak] = useState(0);
  const [activeCard, setActiveCard] = useState(null);

  useEffect(() => {
    const t1 = setTimeout(() => {
      let p = 0;
      const iv = setInterval(() => {
        p += 1;
        setProgress(p);
        if (p >= 72) clearInterval(iv);
      }, 18);
    }, 600);
    const t2 = setTimeout(() => {
      let s = 0;
      const iv = setInterval(() => {
        s += 1;
        setStreak(s);
        if (s >= 12) clearInterval(iv);
      }, 60);
    }, 800);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  return (
    <motion.div className="hero-visual" variants={heroItem}>
      <div className="hero-visual-glow" aria-hidden="true" />

      <div className="hv-panel">
        {/* Floating badge */}
        <div className="hv-badge">
          <span className="hv-badge-dot" />
          Live Progress
        </div>

        {/* Progress ring header */}
        <div className="hv-header">
          <div className="hv-header-left">
            <span className="hv-avatar">📚</span>
            <div>
              <div className="hv-title">Your Learning Journey</div>
              <div className="hv-subtitle">Track your progress</div>
            </div>
          </div>
          <div className="hv-ring-wrap">
            <svg viewBox="0 0 56 56" className="hv-ring">
              <circle cx="28" cy="28" r="22" fill="none" stroke="rgba(109,93,252,0.15)" strokeWidth="5" />
              <circle
                cx="28" cy="28" r="22" fill="none"
                stroke="url(#ringGrad)" strokeWidth="5"
                strokeLinecap="round"
                strokeDasharray={`${2 * Math.PI * 22}`}
                strokeDashoffset={`${2 * Math.PI * 22 * (1 - progress / 100)}`}
                style={{ transition: "stroke-dashoffset 0.04s linear", transform: "rotate(-90deg)", transformOrigin: "center" }}
              />
              <defs>
                <linearGradient id="ringGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#7c3aed" />
                  <stop offset="100%" stopColor="#00a6ff" />
                </linearGradient>
              </defs>
            </svg>
            <span className="hv-ring-pct">{progress}%</span>
          </div>
        </div>

        {/* Mini stats */}
        <div className="hv-stats-row">
          <div className="hv-mini-stat">
            <span className="hv-mini-icon" style={{ background: "rgba(109,93,252,0.12)" }}>⏱</span>
            <div>
              <div className="hv-mini-val">48/67</div>
              <div className="hv-mini-label">Lessons Done</div>
            </div>
          </div>
          <div className="hv-mini-stat">
            <span className="hv-mini-icon" style={{ background: "rgba(20,184,106,0.12)" }}>🔥</span>
            <div>
              <div className="hv-mini-val">{streak} days</div>
              <div className="hv-mini-label">Streak</div>
            </div>
          </div>
          <div className="hv-mini-stat">
            <span className="hv-mini-icon" style={{ background: "rgba(245,185,21,0.14)" }}>⭐</span>
            <div>
              <div className="hv-mini-val">24h 36m</div>
              <div className="hv-mini-label">Study Time</div>
            </div>
          </div>
        </div>

        {/* Skill bars */}
        <div className="hv-skills">
          <div className="hv-section-title">Skills You'll Master</div>
          {SKILL_BARS.map((skill, i) => (
            <div
              key={skill.label}
              className={`hv-skill-row${activeCard === i ? " hv-skill-active" : ""}`}
              onMouseEnter={() => setActiveCard(i)}
              onMouseLeave={() => setActiveCard(null)}
            >
              <span className="hv-skill-icon">{skill.icon}</span>
              <span className="hv-skill-label">{skill.label}</span>
              <AnimatedBar pct={skill.pct} color={skill.color} delay={i * 160} />
              <span className="hv-skill-pct" style={{ color: skill.color }}>{skill.pct}%</span>
            </div>
          ))}
        </div>

        {/* Typing code block */}
        <TypingCode />
      </div>
    </motion.div>
  );
}

/* ─────────────────────────────────────────
   COURSE CARD
───────────────────────────────────────── */
function CourseCard({ course, index }) {
  const navigate = useNavigate();

  const openCourse = useCallback(() => {
    navigate(course.route);
  }, [navigate, course.route]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openCourse();
      }
    },
    [openCourse]
  );

  return (
    <motion.article
      className="course-card"
      custom={index}
      variants={revealCard}
      initial="hidden"
      whileInView="show"
      viewport={viewport}
      whileHover={{ y: -8, transition: { duration: 0.28, ease: "easeOut" } }}
      onClick={openCourse}
      tabIndex={0}
      aria-label={`Open ${course.title}`}
      onKeyDown={handleKeyDown}
    >
      {/* Media */}
      <div className="course-media">
        <img src={course.image} alt={course.title} loading="lazy" />
        <span className="course-tag" style={{ background: course.tagColor }}>
          {course.tag}
        </span>
        <div className="course-media-overlay" aria-hidden="true" />
      </div>

      {/* Body */}
      <div className="course-content">
        <div className="course-meta">
          <span className="course-level">{course.level}</span>
          <span className="course-icon" aria-hidden="true">{course.icon}</span>
        </div>

        <h3>{course.title}</h3>
        <p>{course.desc}</p>

        <div className="course-topics">
          {course.topics.map((topic) => (
            <span key={topic} className="topic-chip">{topic}</span>
          ))}
        </div>

        <button
          className="course-cta"
          type="button"
          onClick={(e) => { e.stopPropagation(); openCourse(); }}
        >
          <span>Start Learning</span>
          <ArrowIcon />
        </button>
      </div>
    </motion.article>
  );
}

/* ─────────────────────────────────────────
   MAIN HOME COMPONENT
───────────────────────────────────────── */
function Home() {
  const [skillIndex, setSkillIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const shouldReduceMotion = useReducedMotion();

  const activeSkill = useMemo(() => SKILLS[skillIndex], [skillIndex]);

  /* SEO */
  useSEO({
    title: "NextSkillAI — Learn Excel, SQL, Python and Data Skills",
    description:
      "NextSkillAI helps beginners learn practical Excel, SQL, Python, Power BI and analytics skills through real-world learning paths.",
    keywords:
      "NextSkillAI, Excel tutorials, SQL tutorials, Python tutorials, data analytics, Power BI, beginner data skills",
    canonical: "/",
    ogType: "website",
    schema: {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "NextSkillAI",
      url: "https://nextskillai.com",
      description:
        "Practical tutorials for Excel, SQL, Python, Power BI and data analytics beginners.",
    },
  });

  /* Skill rotation */
  useEffect(() => {
    if (shouldReduceMotion) return;

    let innerTimeout = null;
    const id = setInterval(() => {
      setIsAnimating(true);
      innerTimeout = setTimeout(() => {
        setSkillIndex((prev) => (prev + 1) % SKILLS.length);
        setIsAnimating(false);
      }, 220);
    }, 2400);

    return () => {
      clearInterval(id);
      if (innerTimeout) clearTimeout(innerTimeout);
    };
  }, [shouldReduceMotion]);

  return (
    <main className="home">

      {/* ══════════════════════════════════
          HERO
      ══════════════════════════════════ */}
      <section className="home-hero" aria-label="Hero">
        {/* Background decorations */}
        <div className="hero-orb hero-orb-1" aria-hidden="true" />
        <div className="hero-orb hero-orb-2" aria-hidden="true" />
        <div className="hero-orb hero-orb-3" aria-hidden="true" />
        <div className="hero-grid-glow"     aria-hidden="true" />

        <div className="home-shell">
          <motion.div
            className="hero-layout"
            variants={heroContainer}
            initial="hidden"
            animate="show"
          >
            {/* ── Left column ── */}
            <div className="hero-content">
              

              <motion.h1 className="hero-headline" variants={heroItem}>
                <span className="hero-line">Master Data Skills</span>
                <span className="hero-line">That Actually Get</span>
                <span className="hero-line hero-line-bottom">
                  You Hired in{" "}
                  <span className="hero-skill-word-wrap">
                    <span
                      className={`hero-skill-word${
                        isAnimating ? " skill-exit" : " skill-enter"
                      }`}
                    >
                      {activeSkill}
                    </span>
                  </span>
                </span>
              </motion.h1>

              <motion.p className="hero-sub" variants={heroItem}>
                Built for beginners who want real results — not just
                certificates. Learn Excel, SQL, Python, and Power BI through
                practical lessons, real workflows, and skills companies actually
                value.
              </motion.p>

              <motion.div className="hero-actions" variants={heroItem}>
                <Link to="/tutorials" className="btn-primary hero-btn-primary">
                  <span>Start Learning Free</span>
                  <ArrowIcon />
                </Link>
                <Link to="/about" className="btn-secondary hero-btn-secondary">
                  <span>View Learning Paths</span>
                  <ArrowIcon />
                </Link>
              </motion.div>

              {/* Stats card */}
              <motion.div
                className="hero-stats hero-stats-card"
                variants={heroItem}
              >
                {STATS.map((stat) => (
                  <div className="hero-stat" key={stat.label}>
                    <span className="hero-stat-icon" aria-hidden="true">
                      {stat.icon}
                    </span>
                    <span className="hero-stat-copy">
                      <strong>
                        {stat.value}
                        <span>{stat.suffix}</span>
                      </strong>
                      <p>{stat.label}</p>
                    </span>
                  </div>
                ))}
              </motion.div>
            </div>

            {/* ── Right column ── */}
            <HeroVisual />
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════
          TRUST STRIP
      ══════════════════════════════════ */}
      <div className="trust-strip" aria-label="Skills covered">
        <div className="trust-strip-track">
          {TRUST_ITEMS.map((item, i) => (
            <span key={`${item.label}-${i}`} className="trust-chip">
              <img src={item.icon} alt="" aria-hidden="true" />
              {item.label}
            </span>
          ))}
        </div>
      </div>

      {/* ══════════════════════════════════
          FEATURES
      ══════════════════════════════════ */}
      <section className="home-section features-section" aria-label="Why NextSkillAI">
        <div className="home-section-inner">
          <SectionLabel>Why NextSkillAI</SectionLabel>

          <div className="section-head">
            <h2>
              Designed for{" "}
              <span className="gradient-text">Practical Growth</span>
            </h2>
            <p>
              Learn exactly what real learners need to become confident with
              modern data tools — step by step, without fluff.
            </p>
          </div>

          <div className="features-grid">
            {FEATURES.map((item, index) => (
              <motion.div
                className="feature-card"
                key={item.title}
                custom={index}
                variants={revealCard}
                initial="hidden"
                whileInView="show"
                viewport={viewport}
                style={{ "--feature-accent": item.accent }}
              >
                <div className="feature-icon-wrap" aria-hidden="true">
                  <span className="feature-icon">{item.icon}</span>
                </div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
                <div
                  className="feature-accent-line"
                  style={{ background: item.accent }}
                  aria-hidden="true"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════
          COURSES
      ══════════════════════════════════ */}
      <section className="home-section courses-section" aria-label="Featured Learning Tracks">
        <div className="home-section-inner">
          <SectionLabel>Featured Tracks</SectionLabel>

          <div className="section-head">
            <h2>
              Featured{" "}
              <span className="gradient-text">Learning Tracks</span>
            </h2>
            <p>
              Start with the fundamentals, build momentum, and grow into the
              practical skills used in real data careers.
            </p>
          </div>

          <div className="courses-grid">
            {COURSES.map((course, index) => (
              <CourseCard course={course} index={index} key={course.id} />
            ))}
          </div>

          <div className="courses-cta">
            <Link to="/tutorials" className="btn-ghost">
              Browse All Tutorials <ArrowIcon />
            </Link>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════
          OUTCOMES
      ══════════════════════════════════ */}
      <section className="home-section outcomes-section" aria-label="Career Paths">
        <div className="home-section-inner">
          <SectionLabel>Career Paths</SectionLabel>

          <div className="section-head">
            <h2>
              What You Can{" "}
              <span className="gradient-text">Become</span>
            </h2>
            <p>
              NextSkillAI is built to help you move toward real opportunities,
              real projects, and real confidence in your career journey.
            </p>
          </div>

          <div className="outcomes-grid">
            {OUTCOMES.map((item, index) => (
              <motion.div
                className="outcome-card"
                key={item.title}
                custom={index}
                variants={revealCard}
                initial="hidden"
                whileInView="show"
                viewport={viewport}
                style={{ "--outcome-accent": item.accent }}
              >
                <div className="outcome-top">
                  <span className="outcome-icon" aria-hidden="true">
                    {item.icon}
                  </span>
                  <span className="outcome-tag">{item.tag}</span>
                </div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════
          PATH / ROADMAP
      ══════════════════════════════════ */}
      <section className="home-section path-section" aria-label="Your Roadmap">
        <div className="home-section-inner">
          <SectionLabel>Your Roadmap</SectionLabel>

          <div className="section-head">
            <h2>
              A Simpler Path to{" "}
              <span className="gradient-text">Job-Ready Skills</span>
            </h2>
            <p>
              Three focused stages help you move from confusion to clarity, and
              from basic learning to practical confidence.
            </p>
          </div>

          <div className="path-grid">
            {PATH.map((item, index) => (
              <motion.div
                className="path-step"
                key={item.no}
                custom={index}
                variants={revealCard}
                initial="hidden"
                whileInView="show"
                viewport={viewport}
              >
                <div className="path-number" aria-hidden="true">{item.no}</div>
                <div className="path-body">
                  <h3>{item.title}</h3>
                  <p>{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════
          FINAL CTA
      ══════════════════════════════════ */}
      <section className="final-cta" aria-label="Get Started Today">
        <div className="final-cta-inner">
          <div className="final-cta-orb final-cta-orb-1" aria-hidden="true" />
          <div className="final-cta-orb final-cta-orb-2" aria-hidden="true" />

          <div className="final-cta-content">
            <span className="section-label cta-label">Get Started Today</span>

            <h2>
              Start Your Data Career —{" "}
              <span className="gradient-text">Not Someday</span>
            </h2>

            <p>
              Join a learning path built around practical results, real skills,
              and the confidence to grow step by step.
            </p>

            <div className="cta-actions">
              <Link to="/tutorials" className="btn-primary">
                <span>Explore Tutorials</span>
                <ArrowIcon />
              </Link>
              <Link to="/about" className="btn-secondary">
                Learn More
              </Link>
            </div>
          </div>
        </div>
      </section>

    </main>
  );
}

export default Home;